import { SegmentedControl as MantineSegmentedControl } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import type {
    CustomSegmentedControlCheckboxFactory,
    CustomSegmentedControlCheckboxFormikFactory,
    CustomSegmentedControlFactory,
    CustomSegmentedControlFormikFactory,
    IProps,
    ISegmentedControlCheckboxFormikProps,
    ISegmentedControlCheckboxProps,
    ISegmentedControlFormikProps,
} from "./segmented-control.types";
import styles from "./segmented-control.module.scss";
import { useField, useFormikContext } from "formik";

const SegmentedControl = componentWrapper<CustomSegmentedControlFactory, IProps>("SegmentedControl", (props, ref) => {
    return (
        <MantineSegmentedControl
            withItemsBorders={false}
            classNames={{
                root: styles["root"],
                control: styles["control"],
                input: styles["input"],
                label: styles["label"],
                indicator: styles["indicator"],
                innerLabel: styles["innerLabel"],
            }}
            ref={ref}
            {...props}
        />
    );
});

const SegmentedControlFormik = componentWrapper<CustomSegmentedControlFormikFactory, ISegmentedControlFormikProps>(
    "SegmentedControl.Formik",
    ({ name, validate, ...otherProps }, ref) => {
        const [field] = useField({ name, validate });
        return <SegmentedControl ref={ref} {...otherProps} {...field} />;
    }
);

const SegmentedControlCheckbox = componentWrapper<
    CustomSegmentedControlCheckboxFactory,
    ISegmentedControlCheckboxProps
>("SegmentedControlCheckbox", ({ leftLabel, rightLabel, onChange, checked, ...otherProps }, ref) => {
    return (
        <SegmentedControl
            data={[
                { value: "false", label: leftLabel },
                { value: "true", label: rightLabel },
            ]}
            classNames={{
                root: styles["root"],
                control: styles["control"],
                input: styles["input"],
                label: styles["label"],
                indicator: styles["indicator"],
                innerLabel: styles["innerLabel"],
            }}
            value={checked ? "true" : "false"}
            onChange={(value) => {
                if (typeof onChange === "function") {
                    onChange(value === "true");
                }
            }}
            ref={ref}
            {...otherProps}
        />
    );
});

const SegmentedControlCheckboxFormik = componentWrapper<
    CustomSegmentedControlCheckboxFormikFactory,
    ISegmentedControlCheckboxFormikProps
>("SegmentedControlCheckbox.Formik", ({ name, validate, onChange, ...otherProps }, ref) => {
    const [{ onChange: fieldOnChange, value, ...field }] = useField({ name, type: "checkbox", validate });
    const { setFieldValue } = useFormikContext();
    return (
        <SegmentedControlCheckbox
            ref={ref}
            {...otherProps}
            onChange={(value) => {
                setFieldValue(name, value);
                onChange?.(value);
            }}
            {...field}
        />
    );
});

SegmentedControlCheckbox.Formik = SegmentedControlCheckboxFormik;

SegmentedControl.Checkbox = SegmentedControlCheckbox;
SegmentedControl.Formik = SegmentedControlFormik;

export { SegmentedControl, SegmentedControlFormik, SegmentedControlCheckbox, SegmentedControlCheckboxFormik };
